/**
 * Theme Showcase Components
 * A visual demonstration of the application's theme system including colors, typography, borders, and component examples
 * Displays various theme elements in an organized, interactive layout
 */

import React from "react";

/**
 * ThemeShowcase Component
 * A comprehensive display of the application's theme system and design elements
 * Includes sections for colors, typography, borders, and example components
 *
 * The component is organized into distinct sections:
 * - Colors: Displays primary, secondary, accent, and neutral color swatches
 * - Typography: Shows different text styles and their usage
 * - Borders: Demonstrates border styles and applications
 * - Components: Provides examples of themed UI components
 *
 * @returns {JSX.Element} A complete theme showcase layout with interactive examples
 */
const ThemeShowcase = () => {
  return (
    <div className="p-8 space-y-8">
      <h1 className="text-3xl font-bold text-theme-primary mb-8">
        Theme Showcase
      </h1>

      {/* Colors Section */}
      <section>
        <h2 className="text-2xl font-semibold text-theme-primary mb-4">
          Colors
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-white rounded-lg shadow">
            <div className="w-full h-24 bg-theme-primary rounded-lg mb-2"></div>
            <p className="text-sm font-medium">Primary Color</p>
            <p className="text-xs text-theme-secondary">
              Background & UI elements
            </p>
          </div>
          <div className="p-4 bg-white rounded-lg shadow">
            <div className="w-full h-24 bg-theme-secondary rounded-lg mb-2"></div>
            <p className="text-sm font-medium">Secondary Color</p>
            <p className="text-xs text-theme-secondary">
              Alternative backgrounds
            </p>
          </div>
          <div className="p-4 bg-white rounded-lg shadow">
            <div className="w-full h-24 bg-theme-accent rounded-lg mb-2"></div>
            <p className="text-sm font-medium">Accent Color</p>
            <p className="text-xs text-theme-secondary">Highlights & CTAs</p>
          </div>
          <div className="p-4 bg-white rounded-lg shadow">
            <div className="w-full h-24 bg-theme-neutral rounded-lg mb-2"></div>
            <p className="text-sm font-medium">Neutral Color</p>
            <p className="text-xs text-theme-secondary">Subtle backgrounds</p>
          </div>
        </div>
      </section>

      {/* Typography Section */}
      <section>
        <h2 className="text-2xl font-semibold text-theme-primary mb-4">
          Typography
        </h2>
        <div className="p-6 bg-white rounded-lg shadow space-y-4">
          <div>
            <h3 className="text-lg font-medium mb-2">Primary Text</h3>
            <p className="text-theme-primary">
              This is primary text, used for most content on the page. It should
              have good contrast with the background.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium mb-2">Secondary Text</h3>
            <p className="text-theme-secondary">
              Secondary text is used for less important content, like
              descriptions and helper text.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium mb-2">Accent Text</h3>
            <p className="text-theme-accent">
              Accent text draws attention to important elements and is often
              used for links and highlights.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium mb-2">Neutral Text</h3>
            <p className="text-theme-neutral">
              Neutral text is used for supporting content that shouldn't draw
              too much attention.
            </p>
          </div>
        </div>
      </section>

      {/* Borders Section */}
      <section>
        <h2 className="text-2xl font-semibold text-theme-primary mb-4">
          Borders
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 border-2 border-theme-primary rounded-lg bg-white">
            <p className="text-sm font-medium">Primary Border</p>
            <p className="text-xs text-theme-secondary">Main containers</p>
          </div>
          <div className="p-4 border-2 border-theme-secondary rounded-lg bg-white">
            <p className="text-sm font-medium">Secondary Border</p>
            <p className="text-xs text-theme-secondary">Subtle separators</p>
          </div>
          <div className="p-4 border-2 border-theme-accent rounded-lg bg-white">
            <p className="text-sm font-medium">Accent Border</p>
            <p className="text-xs text-theme-secondary">Focus states</p>
          </div>
          <div className="p-4 border-2 border-theme-neutral rounded-lg bg-white">
            <p className="text-sm font-medium">Neutral Border</p>
            <p className="text-xs text-theme-secondary">Subtle borders</p>
          </div>
        </div>
      </section>

      {/* Components Examples */}
      <section>
        <h2 className="text-2xl font-semibold text-theme-primary mb-4">
          Component Examples
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Button Examples */}
          <div className="p-6 bg-white rounded-lg shadow">
            <h3 className="text-lg font-medium mb-4">Buttons</h3>
            <div className="space-y-4">
              <button className="w-full px-4 py-2 bg-theme-accent text-white rounded-lg hover:opacity-90">
                Primary Button
              </button>
              <button className="w-full px-4 py-2 bg-theme-secondary text-theme-primary rounded-lg border border-theme-primary hover:bg-theme-neutral">
                Secondary Button
              </button>
            </div>
          </div>

          {/* Card Example */}
          <div className="p-6 bg-white rounded-lg shadow">
            <h3 className="text-lg font-medium mb-4">Card Example</h3>
            <div className="bg-theme-secondary p-4 rounded-lg border border-theme-neutral">
              <h4 className="text-theme-primary font-medium mb-2">
                Card Title
              </h4>
              <p className="text-theme-secondary text-sm mb-4">
                This is an example of how the theme colors work together in a
                card component.
              </p>
              <div className="border-t border-theme-neutral pt-4">
                <span className="text-theme-accent text-sm">Read more →</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThemeShowcase;
